import Utils from '../../Utils';

// Messages from https://github.com/symfony/validator/blob/3.4/Resources/translations/validators.fr.xlf
const VALIDATION_MESSAGES = {
    required: 'Cette valeur ne doit pas être vide.',
    min: 'Cette valeur doit être de {{ limit }} ou plus.',
    minlength: 'Cette chaîne est trop courte. Elle doit avoir au minimum {{ limit }} caractères.',
    max: 'Cette valeur doit être de {{ limit }} ou moins.',
    maxlength: 'Cette chaîne est trop longue. Elle doit avoir au maximum {{ limit }} caractères.',
    pattern: 'Le format de cette chaîne est invalide.',
    email: 'Cette valeur n\'est pas une adresse email valide.',
    email_confirm: 'Cette adresse email ne correspond pas.',
    url: 'Cette valeur n\'est pas une URL valide.',
    search: 'Veuillez saisir un mot-clé.',
};

const VALIDATION_MESSAGES_WITH_LABELS = {
    required: 'Le champ "{{ label }}" ne doit pas être vide.',
    min: 'Le champ "{{ label }}" doit être de {{ limit }} ou plus.',
    minlength: 'Le champ "{{ label }}" est trop court. Il doit avoir au minimum {{ limit }} caractères.',
    max: 'Le champ "{{ label }}" doit être de {{ limit }} ou moins.',
    maxlength: 'Le champ "{{ label }}" est trop long. Il doit avoir au maximum {{ limit }} caractères.',
    pattern: 'Le format du champ "{{ label }}" est invalide.',
    email: 'La valeur du champ "{{ label }}" n\'est pas une adresse email valide.',
    email_confirm: 'La valeur du champ "{{ label }}" ne correspond pas.',
    url: 'La valeur du champ "{{ label }}" n\'est pas une URL valide.',
    search: 'Le champ "{{ label }}" ne doit pas être vide. Veuillez saisir un mot-clé.'
};

export default class FormValidation {

    static initializeEvents() {
        this.addEventsToAllForms();
        this.addEventsToAllInputs();
    }

    static addEventsToAllForms() {
        Array.from(document.querySelectorAll('form')).forEach(form => {
            if (form.querySelectorAll('.input-group').length >= 1) {
                form.addEventListener('submit', event => {
                    Array
                        .from(event.currentTarget.querySelectorAll('.input-group:not(.input-group-without-validation)'))
                        .forEach(inputGroup => {
                            /** @type {HTMLInputElement|null} */
                            const input = inputGroup.querySelector('input[name], select[name], textarea[name]');
                            if (input) {
                                if (this.checkRulesForInput(inputGroup, input) !== true) {
                                    event.preventDefault();
                                }
                            }
                        });
                });
            }
        });

        return this;
    }

    static addEventsToAllInputs() {
        Array.from(document.querySelectorAll('.input-group:not(.input-group-without-validation)')).forEach(inputGroup => {
            inputGroup.addEventListener('focusout', event => {
                event.preventDefault();

                /** @type {HTMLInputElement|null} */
                const input = event.currentTarget.querySelector('input[name], select[name], textarea[name]');
                if (input) {
                    this.checkRulesForInput(inputGroup, input);
                }
            });
        });

        return this;
    }

    /**
     * @param {HTMLDivElement} inputGroup
     * @param {HTMLInputElement} input
     *
     * @return {Boolean|void}
     */
    static checkRulesForInput(inputGroup, input) {
        // Required
        if (input.hasAttribute('required') && !input.hasAttribute('disabled') && Utils.parent(input, '.hidden') === null) {
            // Without checked option
            if (input.getAttribute('type') === 'radio' && !document.querySelector(`input[name="${input.name}"]:checked`)) {
                return this.addErrorToInputGroup(inputGroup, input, 'required');
            }

            // Search
            if (input.classList.contains('search-ui-text')) {
                return this.addErrorToInputGroup(inputGroup, input, 'search');
            }

            // Empty value
            if (input.value === '') {
                return this.addErrorToInputGroup(inputGroup, input, 'required');
            }
        } else if (input.value === '') {
            return this.removeErrorFromInputGroup(inputGroup);
        }

        // Min
        if (input.hasAttribute('min') && parseInt(input.value) < input.getAttribute('min')) {
            return this.addErrorToInputGroup(inputGroup, input, 'min', {
                limit: input.getAttribute('min')
            });
        }

        // Min Length
        if (input.hasAttribute('minlength') && input.value.length < input.getAttribute('minlength')) {
            return this.addErrorToInputGroup(inputGroup, input, 'minlength', {
                limit: input.getAttribute('minlength')
            });
        }

        // Max
        if (input.hasAttribute('max') && parseInt(input.value) > input.getAttribute('max')) {
            return this.addErrorToInputGroup(inputGroup, input, 'max', {
                limit: input.getAttribute('max')
            });
        }

        // Max length
        if (input.hasAttribute('maxlength') && input.value.length > input.getAttribute('maxlength')) {
            return this.addErrorToInputGroup(inputGroup, input, 'maxlength', {
                limit: input.getAttribute('maxlength')
            });
        }

        // Regex
        if (input.hasAttribute('pattern') && !((new RegExp(`^${input.getAttribute('pattern')}$`)).test(input.value))) {
            return this.addErrorToInputGroup(inputGroup, input, 'pattern', {
                pattern: input.getAttribute('pattern')
            });
        }

        // Email
        if (input.getAttribute('type') === 'email' && !(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(input.value))) {
            if (input.id.endsWith('_confirmation')) {
                const confirmableInputId = input.id.slice(0, -13).split('-').slice(1).join('-');
                const confirmableInput = document.querySelector('input[type="email"][id$="-' + confirmableInputId + '"]');

                if (confirmableInput && input.value !== confirmableInput.value) {
                    return this.addErrorToInputGroup(inputGroup, input, 'email_confirm');
                }
            }

            return this.addErrorToInputGroup(inputGroup, input, 'email');
        }

        // URL (https://regex101.com/r/bYVoMt/1)
        if (input.getAttribute('type') === 'url' && !(/^https?:\/\/[^\s]{3,}\.[^\s]{2,}$/.test(input.value))) {
            return this.addErrorToInputGroup(inputGroup, input, 'url');
        }

        return this.removeErrorFromInputGroup(inputGroup);
    }

    /**
     * @param {HTMLDivElement} inputGroup
     * @param {HTMLInputElement} input
     * @param {String} messageKey
     * @param {Object} replacements
     *
     * @return {boolean}
     */
    static addErrorToInputGroup(inputGroup, input, messageKey, replacements = {}) {
        const label = inputGroup.querySelector('label[for="' + input.id + '"]');
        const validationMessages = label ? VALIDATION_MESSAGES_WITH_LABELS : VALIDATION_MESSAGES;

        let message = validationMessages[messageKey];
        Object.keys(replacements).forEach(search => message = message.replace(`{{ ${search} }}`, replacements[search]));
        if (label) {
            message = message.replace(`{{ label }}`, label.innerText.trim());

            if (input.hasAttribute('data-example')) {
                message += ' Exemple : ' + input.getAttribute('data-example') + '.';
            }
        }

        // Remove current error
        this.removeErrorFromInputGroup(inputGroup);

        // Add new error
        const errorWrapper = document.createElement('p');
        errorWrapper.id = Utils.generateUniqueIdentifier('input-error-message');
        errorWrapper.classList.add('input-error-text', 'mb-0');

        errorWrapper.innerText = message;

        inputGroup.classList.add('input-group-error');
        inputGroup.appendChild(errorWrapper);

        input.setAttribute('aria-invalid', 'true');
        input.setAttribute('aria-errormessage', errorWrapper.id);
        input.setAttribute('role', 'alert');
        input.setAttribute('aria-live', 'polite');

        return false;
    }

    /**
     * @param {HTMLDivElement} inputGroup
     *
     * @return {boolean}
     */
    static removeErrorFromInputGroup(inputGroup) {
        inputGroup.classList.remove('input-group-error');

        const currentInput = inputGroup.querySelector('[aria-invalid], [aria-invalid]');
        if (currentInput) {
            currentInput.removeAttribute('aria-invalid');
            currentInput.removeAttribute('aria-errormessage');
            currentInput.removeAttribute('aria-live');
            currentInput.removeAttribute('role');
        }

        const currentErrorWrapper = inputGroup.querySelector('.input-error-text');
        if (currentErrorWrapper) {
            inputGroup.removeChild(currentErrorWrapper);
        }

        return true;
    }

}
